import type {
  CameraVideoTrackInitConfig,
  ILocalTrack,
  IRemoteTrack,
  MicrophoneAudioTrackInitConfig,
} from 'agora-rtc-sdk-ng'

import { isMobileWeb } from '@core/userAgent'
import { getNextJsPublicEnv } from '@src/helpers'

import { ENCODER_CONFIG_BASE } from './constants'

/**
 * Lazy loads AgoraRTC package.
 * @return AgoraRTC module.
 */
export function loadAgoraRTC() {
  return import('agora-rtc-sdk-ng').then(({ default: AgoraRTC }) => AgoraRTC)
}

/**
 * Lazy loads VirtualBackgroundExtension package.
 * @return VirtualBackgroundExtension class.
 */
export function loadVirtualBackgroundExtension() {
  return import('agora-extension-virtual-background').then(
    ({ default: VirtualBackgroundExtension }) => VirtualBackgroundExtension,
  )
}

export const agoraAppId =
  getNextJsPublicEnv('AGORA_APP_ID') ?? process.env.AGORA_APP_ID

/** Creates a microphone audio track. */
export async function createMicrophoneAudioTrack(
  config?: MicrophoneAudioTrackInitConfig,
) {
  const AgoraRTC = await loadAgoraRTC()
  return AgoraRTC.createMicrophoneAudioTrack(config)
}

const DEFAULT_ENCODER_CONFIG = {
  ...ENCODER_CONFIG_BASE,
  width: isMobileWeb() ? 1280 : 720,
  height: isMobileWeb() ? 720 : 1280,
}

/** Creates a camera video track. */
export async function createCameraVideoTrack(
  config?: CameraVideoTrackInitConfig,
) {
  const { encoderConfig, ...rest } = config ?? {}
  const fullConfig = {
    encoderConfig: encoderConfig ?? DEFAULT_ENCODER_CONFIG,
    ...rest,
  }

  const AgoraRTC = await loadAgoraRTC()
  return AgoraRTC.createCameraVideoTrack(fullConfig)
}

/** Gets the list of devices. */
export async function getDevices(kind?: MediaDeviceKind) {
  const AgoraRTC = await loadAgoraRTC()

  switch (kind) {
    case 'videoinput':
      return AgoraRTC.getCameras()
    case 'audioinput':
      return AgoraRTC.getMicrophones()
    default:
      return AgoraRTC.getDevices()
  }
}

/** @return Whether the track is a local track. */
export function isLocalTrack(
  track: ILocalTrack | IRemoteTrack | undefined,
): track is ILocalTrack {
  return !!track && 'enabled' in track
}
